import React, { useMemo } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
} from 'chart.js';
import cx from 'classnames';
import { Line } from 'react-chartjs-2';
import withStyles from 'isomorphic-style-loader/withStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import { resolveCurrencySymbol } from './common/resolveCurrencySymbol';
import { CHART_TOOLTIP_CONTENT_STYLE } from './common/chartConstants';
import { parseDateValue, formatChartLabel } from './common/dateUtils';
import { formatNumber } from '../../../helpers/FormatNumber/formatNumber';
import { getTimezoneOffsetLabel } from '../../../helpers/timezoneHelper';
import ChartSection from './common/ChartSection';
import messages from '../../../locale/jsonMessages';
import s from './AdminDashboard.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend
);

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Returns Monday 00:00:00 of the ISO week containing the given date. */
function getWeekStart(date) {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    const dayOfWeek = d.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    d.setDate(d.getDate() + diff);
    return d;
}

const RevenuePerformanceChart = ({
    monthlyTotals = [],
    dateStart,
    dateEnd,
    filterOption,
    baseCurrencySymbol
}) => {
    const intl = useIntl();
    const currencySymbol = resolveCurrencySymbol(baseCurrencySymbol, intl.locale || 'en-US');
    const totalAmountLabel = intl.formatMessage(messages.totalAmountLabel);
    const serviceFeesRenterOwnerLabel = intl.formatMessage(messages.serviceFeesRenterOwner);

    const timezoneLabel = useMemo(() => getTimezoneOffsetLabel(), []);

    const rangeType = useMemo(() => {
        if (filterOption === 'last24Hours') return 'hours';
        const start = parseDateValue(dateStart);
        const end = parseDateValue(dateEnd);
        if (!start || !end) return 'month';
        const sameDay = start.toDateString() === end.toDateString();
        const diffMs = end.getTime() - start.getTime();
        const rangeDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        if (rangeDays <= 1) {
            if (filterOption === 'custom') {
                return sameDay ? 'hours' : 'dayMonth';
            }
            return 'hours';
        }
        if (rangeDays > 7 && rangeDays < 60) return 'week';
        if (rangeDays < 60) return 'dayMonth';
        return 'month';
    }, [dateStart, dateEnd, filterOption]);

    const rangeMeta = useMemo(() => {
        const start = parseDateValue(dateStart);
        const end = parseDateValue(dateEnd);
        if (!start || !end) {
            return { startDayTime: null, rangeDays: null };
        }
        const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0);
        const diffMs = end.getTime() - start.getTime();
        const rangeDays = Math.ceil(diffMs / MS_PER_DAY);
        return { startDayTime: startDay.getTime(), rangeDays };
    }, [dateStart, dateEnd]);

    const chartData = useMemo(() => {
        if (!Array.isArray(monthlyTotals)) return [];
        const dataMap = new Map();
        monthlyTotals.forEach((row) => {
            const ta = Number(row.totalAmount) || 0;
            const g = Number(row.totalGuestServiceFee) || 0;
            const h = Number(row.totalHostServiceFee) || 0;
            const rowDate = parseDateValue(row.month);
            const key = rowDate ? rowDate.getTime() : row.month;

            dataMap.set(key, {
                period: rowDate ? rowDate.getTime() : row.month,
                totalAmount: ta,
                combinedServiceFees: g + h,
                totalGuestServiceFee: g,
                totalHostServiceFee: h,
            });
        });

        const start = parseDateValue(dateStart);
        const end = parseDateValue(dateEnd);
        if (!start || !end) {
            return Array.from(dataMap.values()).sort((a, b) => {
                if (typeof a.period === 'number' && typeof b.period === 'number') {
                    return a.period - b.period;
                }
                return String(a.period).localeCompare(String(b.period));
            });
        }

        const rangeData = [];
        if (rangeType === 'hours') {
            if (filterOption === 'last24Hours') {
                const endHour = new Date(end);
                endHour.setMinutes(0, 0, 0);
                const startHour = new Date(endHour);
                startHour.setHours(startHour.getHours() - 23);
                for (let current = new Date(startHour); current <= endHour; current.setHours(current.getHours() + 1)) {
                    const key = current.getTime();
                    rangeData.push(dataMap.get(key) || {
                        period: key,
                        totalAmount: 0,
                        combinedServiceFees: 0,
                        totalGuestServiceFee: 0,
                        totalHostServiceFee: 0,
                    });
                }
                return rangeData;
            }
            const dayStart = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0);
            for (let hour = 0; hour < 24; hour += 1) {
                const slot = new Date(dayStart);
                slot.setHours(hour, 0, 0, 0);
                const key = slot.getTime();
                rangeData.push(dataMap.get(key) || {
                    period: key,
                    totalAmount: 0,
                    combinedServiceFees: 0,
                    totalGuestServiceFee: 0,
                    totalHostServiceFee: 0,
                });
            }
            return rangeData;
        }

        if (rangeType === 'week') {
            const weekStart = getWeekStart(start);
            const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 0, 0, 0, 0);
            let currentWeek = new Date(weekStart);
            while (currentWeek <= endDay) {
                const weekStartTime = currentWeek.getTime();
                const nextWeek = new Date(currentWeek);
                nextWeek.setDate(nextWeek.getDate() + 7);
                let totalAmount = 0;
                let totalGuestServiceFee = 0;
                let totalHostServiceFee = 0;
                const dayCursor = new Date(currentWeek);
                while (dayCursor < nextWeek && dayCursor <= endDay) {
                    const dayStart = new Date(dayCursor.getFullYear(), dayCursor.getMonth(), dayCursor.getDate(), 0, 0, 0, 0);

                    const key = dayStart.getTime();
                    const row = dataMap.get(key);
                    if (row) {
                        totalAmount += row.totalAmount || 0;
                        totalGuestServiceFee += row.totalGuestServiceFee || 0;
                        totalHostServiceFee += row.totalHostServiceFee || 0;
                    }

                    dayCursor.setDate(dayCursor.getDate() + 1);
                }
                rangeData.push({
                    period: weekStartTime,
                    totalAmount,
                    combinedServiceFees: totalGuestServiceFee + totalHostServiceFee,
                    totalGuestServiceFee,
                    totalHostServiceFee,
                });
                currentWeek = nextWeek;
            }
            return rangeData;
        }

        if (rangeType === 'dayMonth') {
            const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0);
            const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 0, 0, 0, 0);
            if (endDay < startDay) return rangeData;
            const current = new Date(startDay);
            while (current <= endDay) {
                const key = current.getTime();
                rangeData.push(dataMap.get(key) || {
                    period: key,
                    totalAmount: 0,
                    combinedServiceFees: 0,
                    totalGuestServiceFee: 0,
                    totalHostServiceFee: 0,
                });
                current.setDate(current.getDate() + 1);
            }
            return rangeData;
        }

        if (rangeType === 'month') {
            const startMonth = new Date(start.getFullYear(), start.getMonth(), 1, 0, 0, 0, 0);
            const endMonth = new Date(end.getFullYear(), end.getMonth(), 1, 0, 0, 0, 0);
            const current = new Date(startMonth);
            while (current <= endMonth) {
                const key = current.getTime();
                rangeData.push(dataMap.get(key) || {
                    period: key,
                    totalAmount: 0,
                    combinedServiceFees: 0,
                    totalGuestServiceFee: 0,
                    totalHostServiceFee: 0,
                });
                current.setMonth(current.getMonth() + 1);
            }
            return rangeData;
        }

        return Array.from(dataMap.values()).sort((a, b) => {
            if (typeof a.period === 'number' && typeof b.period === 'number') {
                return a.period - b.period;
            }
            return String(a.period).localeCompare(String(b.period));
        });
    }, [monthlyTotals, dateStart, dateEnd, rangeType, filterOption, timezoneLabel]);

    const tickFormatter = (value) => {
        if (rangeType === 'week') {
            return formatChartLabel(value, 'week');
        }
        const useWeeklyTicks = rangeType === 'dayMonth'
            && rangeMeta.rangeDays > 8
            && rangeMeta.rangeDays < 60
            && rangeMeta.startDayTime;

        if (useWeeklyTicks) {
            const tickTime = typeof value === 'number'
                ? value
                : (parseDateValue(value)?.getTime() || null);
            if (!tickTime) return '';
            const diffDays = Math.round((tickTime - rangeMeta.startDayTime) / MS_PER_DAY);
            if (diffDays < 0 || diffDays % 7 !== 0) return '';
        }
        const baseLabel = formatChartLabel(value, rangeType);
        if (rangeType === 'hours' && timezoneLabel) {
            return `${baseLabel} ${timezoneLabel}`;
        }

        return baseLabel;
    };

    const labels = chartData.map((d) => d.period);
    const lineChartData = {
        labels,
        datasets: [
            {
                label: totalAmountLabel,
                data: chartData.map((d) => d.totalAmount),
                borderColor: '#090b1e',
                backgroundColor: 'rgba(9, 11, 30, 0.1)',
                fill: false,
                tension: 0.2,
                pointBackgroundColor: '#090b1e',
                pointRadius: 4,
                pointHoverRadius: 6,
            },
            {
                label: serviceFeesRenterOwnerLabel,
                data: chartData.map((d) => d.combinedServiceFees),
                borderColor: '#1852d8',
                backgroundColor: 'rgba(24, 82, 216, 0.1)',
                fill: false,
                tension: 0.2,
                pointBackgroundColor: '#1852d8',
                pointRadius: 4,
                pointHoverRadius: 6,
            },
        ],
    };

    const options = useMemo(() => ({
        font: {
            family: "'Outfit', sans-serif",
        },
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            tooltip: {
                enabled: true,
                ...CHART_TOOLTIP_CONTENT_STYLE,
                titleFont: {
                    family: "'Outfit', sans-serif",
                    weight: "600",
                },
                bodyFont: {
                    family: "'Outfit', sans-serif",
                    weight: "700",
                },
                callbacks: {
                    title(items) {
                        if (!items || items.length === 0) return '—';
                        const dataIndex = items[0].dataIndex;
                        const period = chartData[dataIndex]?.period;
                        if (period == null) return '—';
                        let baseTitle = formatChartLabel(period, rangeType, { useFullMonth: true });
                        if (rangeType === 'hours' && timezoneLabel) {
                            return `${baseTitle} ${timezoneLabel}`;
                        }
                        return baseTitle;
                    },
                    label(context) {
                        const label = context.dataset?.label ?? '';
                        const raw = context.raw;
                        if (raw == null || Number.isNaN(Number(raw))) {
                            return `${label}: ${currencySymbol}0`;
                        }
                        return `${label}: ${currencySymbol}${formatNumber(raw.toFixed(2))}`;
                    },
                },
                bodyFont: {
                    weight: '700',
                },
            },
            legend: {
                display: false,
            },
        },
        scales: {
            x: {
                ticks: {
                    font: {
                        family: "'Outfit', sans-serif",
                    },
                    maxRotation: -35,
                    callback(_, index) {
                        const value = chartData[index]?.period;
                        return value != null ? tickFormatter(value) : '';
                    },
                },
                grid: {
                    display: true,
                    drawBorder: true,
                },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    font: {
                        family: "'Outfit', sans-serif",
                    },
                    callback(value) {
                        return formatNumber(value);
                    },
                },
                grid: {
                    display: true,
                },
            },
        },
    }), [chartData, rangeType, currencySymbol]);

    if (!chartData.length) {
        return (
            <div className={cx(s.platformEarningsCard, s.platformEarningsCardNoData)}>
                <h3 className={s.noDataTitle}><FormattedMessage  {...messages.RevenuePerformance} /></h3>
                <p className={s.noDataDescription}><FormattedMessage  {...messages.noDataAvailable} /></p>
            </div>
        );
    }
    return (
        <ChartSection
            title={<FormattedMessage  {...messages.revenuePerformance} />}
            subtitle={<FormattedMessage  {...messages.compareBooking} />}
            containerClass={s.dashboardRevenueCard}
            isRevenueChart={true}
            timezoneLabel={timezoneLabel}
            rangeType={rangeType}
        >
            <div style={{ height: 450 }}>
                <Line data={lineChartData} options={options} />
            </div>
        </ChartSection>
    );
};

export default withStyles(s)(RevenuePerformanceChart);
